// @ts-strict-ignore
import * as React from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from '@/components/styles/globalStyles';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import { useState, type FC } from 'react';
import { filterOptionLabel } from '../../helpers/formHelpers';
import type { AzureActiveDirectoryProviderData, MicrosoftTeamsChannel } from '../../types';

const TEAMS_CHANNEL_SEPARATOR = '#runway#';

interface TeamChannelPair {
    teamId: string | undefined;
    channelIds: string[];
}

function groupValuesByTeam(
    values: string[],
    teams: AzureActiveDirectoryProviderData['availableTeams'],
): TeamChannelPair[] {
    const byTeam = new Map<string, string[]>();
    const noTeam: string[] = [];
    for (const val of values) {
        const parts = val.split(TEAMS_CHANNEL_SEPARATOR);
        if (parts.length === 2) {
            const teamId = parts[0];
            if (!byTeam.has(teamId)) byTeam.set(teamId, []);
            byTeam.get(teamId).push(val);
        } else {
            noTeam.push(val);
        }
    }
    const pairs: TeamChannelPair[] = [];
    for (const [teamId, channelIds] of byTeam) {
        pairs.push({ teamId, channelIds });
    }
    if (noTeam.length > 0) {
        pairs.push({ teamId: undefined, channelIds: noTeam });
    }
    if (pairs.length === 0 && teams.length === 1) {
        pairs.push({ teamId: teams[0].id, channelIds: [] });
    }
    if (pairs.length === 0) {
        pairs.push({ teamId: undefined, channelIds: [] });
    }
    return pairs;
}

function stripCompositePrefix(value: string): string {
    const parts = value.split(TEAMS_CHANNEL_SEPARATOR);
    return parts.length === 2 ? parts[1] : value;
}

function flattenPairsToValues(pairs: TeamChannelPair[]): string[] {
    const result: string[] = [];
    for (const p of pairs) {
        result.push(...p.channelIds);
    }
    return result;
}

interface TeamsChannelOverrideSelectProps {
    allChannels: MicrosoftTeamsChannel[];
    selectedChannels: { id: string; name: string }[];
    teams: AzureActiveDirectoryProviderData['availableTeams'];
    disabled: boolean;
    value?: string[];
    onChange?: (value: string[]) => void;
}

export const TeamsChannelOverrideSelect: FC<TeamsChannelOverrideSelectProps> = ({
    allChannels,
    selectedChannels,
    teams,
    disabled,
    value,
    onChange,
}) => {
    const [pairs, setPairs] = useState<TeamChannelPair[]>(() => groupValuesByTeam(value ?? [], teams));

    const emitChange = (updatedPairs: TeamChannelPair[]) => {
        setPairs(updatedPairs);
        onChange?.(flattenPairsToValues(updatedPairs));
    };

    const channelsForTeam = (teamId: string | undefined) => {
        if (!teamId) return allChannels;
        return allChannels.filter((ch) => {
            const parts = ch.id.split(TEAMS_CHANNEL_SEPARATOR);
            return parts.length === 2 && parts[0] === teamId;
        });
    };

    const handleTeamChange = (pairIndex: number, newTeamId: string | undefined) => {
        const updated = pairs.map((p, i) => (i === pairIndex ? { teamId: newTeamId, channelIds: [] } : p));
        emitChange(updated);
    };

    const handleChannelChange = (pairIndex: number, newValues: string[]) => {
        const pair = pairs[pairIndex];
        const processed = newValues.map((val) => {
            if (val.includes(TEAMS_CHANNEL_SEPARATOR)) return val;
            if (allChannels.some((ch) => ch.id === val)) return val;
            if (pair.teamId) return `${pair.teamId}${TEAMS_CHANNEL_SEPARATOR}${val}`;
            return val;
        });
        const updated = pairs.map((p, i) => (i === pairIndex ? { ...p, channelIds: processed } : p));
        emitChange(updated);
    };

    const addPair = () => {
        emitChange([...pairs, { teamId: undefined, channelIds: [] }]);
    };

    const removePair = (pairIndex: number) => {
        const updated = pairs.filter((_, i) => i !== pairIndex);
        emitChange(updated.length > 0 ? updated : [{ teamId: undefined, channelIds: [] }]);
    };

    const usedTeamIds = new Set(pairs.map((p) => p.teamId).filter(Boolean));

    return (
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
            {pairs.map((pair, index) => {
                const filtered = channelsForTeam(pair.teamId);
                const fromSelected = selectedChannels.filter(
                    (s) => pair.channelIds.includes(s.id) && !filtered.some((f) => f.id === s.id),
                );
                const knownIds = new Set([...filtered.map((c) => c.id), ...fromSelected.map((c) => c.id)]);
                const freeFormOptions = pair.channelIds
                    .filter((id) => !knownIds.has(id))
                    .map((id) => ({ id, name: stripCompositePrefix(id) }));
                const options = [...filtered, ...fromSelected, ...freeFormOptions];
                return (
                    <React.Fragment key={pair.teamId ?? `pair-${index}`}>
                        {index > 0 && (
                            <Text
                                style={{
                                    textAlign: 'center',
                                    display: 'block',
                                    color: antGrey.grey7,
                                    fontWeight: 600,
                                }}
                            >
                                AND
                            </Text>
                        )}
                        <Space direction="vertical" style={{ width: '100%' }} size={4}>
                            <Select
                                placeholder="Select a team"
                                value={pair.teamId}
                                onChange={(val) => handleTeamChange(index, val)}
                                allowClear
                                onClear={() => removePair(index)}
                                showSearch
                                filterOption={filterOptionLabel}
                                disabled={disabled}
                                style={{ width: '100%' }}
                            >
                                {teams
                                    .filter((t) => t.id === pair.teamId || !usedTeamIds.has(t.id))
                                    .map((team) => (
                                        <Select.Option value={team.id} key={team.id} label={team.name}>
                                            {team.name}
                                        </Select.Option>
                                    ))}
                            </Select>
                            <Select
                                placeholder="Select or enter channel(s)"
                                mode="tags"
                                notFoundContent={null}
                                showSearch
                                allowClear
                                filterOption={filterOptionLabel}
                                disabled={disabled || !pair.teamId}
                                value={pair.channelIds}
                                onChange={(vals) => handleChannelChange(index, vals)}
                                style={{ width: '100%' }}
                                tagRender={(tagProps) => {
                                    const label = tagProps.label as string;
                                    const display = typeof label === 'string' ? stripCompositePrefix(label) : label;
                                    return (
                                        <span
                                            className="ant-select-selection-item"
                                            style={{ display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            <span className="ant-select-selection-item-content">{display}</span>
                                            {!disabled && (
                                                <span
                                                    className="ant-select-selection-item-remove"
                                                    onClick={tagProps.onClose}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    ×
                                                </span>
                                            )}
                                        </span>
                                    );
                                }}
                            >
                                {options.map((channel, i) => {
                                    const displayName = stripCompositePrefix(channel.name || channel.id);
                                    return (
                                        <Select.Option value={channel.id} key={i} label={displayName}>
                                            {displayName}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </Space>
                    </React.Fragment>
                );
            })}
            {teams.length > 1 && pairs.length < teams.length && pairs[0]?.teamId && (
                <Button
                    onClick={addPair}
                    disabled={disabled}
                    icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                >
                    Add channel(s) with another team
                </Button>
            )}
        </Space>
    );
};
